import * as React from 'react';
import { useState } from 'react';
import AreaShell from '../../chrome/AreaShell';
import { TCard, TFoot, TRow } from '../../components/table';
import { ConfirmDialog, Dialog, Field, TextInput } from '../../components/forms';
import { ListState, useList, useToast } from '../../components/roster';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import type { WhitelabelSettingsInfo } from '@/src/WhitelabelSettings/lib/types';

/*
 * Custom gallery domains and the sender identity on guest share emails.
 * Hook calls are (orgId, id) as declared; the classic page swapped them.
 */

const EMPTY = {
    name: '',
    domain: '',
    emailFromName: '',
    emailFromAddress: '',
    emailReplyToAddress: '',
};
type Draft = typeof EMPTY;

const stripScheme = (d?: string) => (d ?? '').replace(/^https?:\/\//, '');

/* same rules as v2, reported per field */
function validate(d: Draft): Partial<Record<keyof Draft, string>> {
    const e: Partial<Record<keyof Draft, string>> = {};
    if (d.name.trim().length < 2) e.name = 'Name is required.';
    if (!/^https?:\/\/.+\..+/.test(d.domain.trim()))
        e.domain = 'Use a full URL, e.g. https://photos.example.com';
    if (!d.emailFromAddress.includes('@')) e.emailFromAddress = 'From address must be an email.';
    if (d.emailReplyToAddress && !d.emailReplyToAddress.includes('@'))
        e.emailReplyToAddress = 'Reply-to must be an email.';
    return e;
}

export default function OrgWhitelabelV3() {
    const { orgId } = useUserOrg();
    const {
        getWhitelabelSettingsByOrgId,
        createWhitelabelSettings,
        updateWhitelabelSettings,
        deleteWhitelabelSettings,
    } = useWhitelabelSettings();
    const toast = useToast();
    const list = useList<WhitelabelSettingsInfo>(() => getWhitelabelSettingsByOrgId(orgId), [orgId]);
    const rows = list.rows ?? [];

    const [dialog, setDialog] = useState<{ kind: 'create' } | { kind: 'edit'; row: WhitelabelSettingsInfo } | null>(null);
    const [draft, setDraft] = useState<Draft>(EMPTY);
    const [tried, setTried] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const problems = validate(draft);
    const show = (k: keyof Draft) => (tried ? problems[k] : undefined);

    const openCreate = () => {
        setDraft(EMPTY);
        setTried(false);
        setError(null);
        setDialog({ kind: 'create' });
    };
    const openEdit = (row: WhitelabelSettingsInfo) => {
        setDraft({
            name: row.name ?? '',
            domain: row.domain ?? '',
            emailFromName: row.email_from_name ?? '',
            emailFromAddress: row.email_from_address ?? '',
            emailReplyToAddress: row.email_reply_to_address ?? '',
        });
        setTried(false);
        setError(null);
        setDialog({ kind: 'edit', row });
    };
    const close = () => {
        if (!busy) setDialog(null);
    };

    const submit = async () => {
        if (!dialog || busy) return;
        setTried(true);
        if (Object.keys(problems).length) return;
        setBusy(true);
        setError(null);
        const body = {
            name: draft.name.trim(),
            domain: draft.domain.trim(),
            emailFromName: draft.emailFromName.trim(),
            emailFromAddress: draft.emailFromAddress.trim(),
            emailReplyToAddress: draft.emailReplyToAddress.trim() || draft.emailFromAddress.trim(),
        };
        const ok =
            dialog.kind === 'create'
                ? await createWhitelabelSettings(orgId, { ...body, orgId }).catch(() => null)
                : await updateWhitelabelSettings(orgId, dialog.row.id, body).catch(() => null);
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.show(dialog.kind === 'create' ? `Added ${body.name}.` : 'Saved.');
            list.reload();
        } else {
            setError('Couldn’t save. Try again.');
        }
    };

    const [deleting, setDeleting] = useState<WhitelabelSettingsInfo | null>(null);
    const remove = async (row: WhitelabelSettingsInfo) => {
        const prev = list.rows;
        list.setRows((current) => (current ?? []).filter((r) => r.id !== row.id));
        const ok = await deleteWhitelabelSettings(orgId, row.id).catch(() => false);
        if (ok) toast.show(`Deleted ${row.name}.`);
        else {
            list.setRows(prev);
            toast.show('Couldn’t delete the white-label.');
        }
    };

    const fromLine = (r: { email_from_name?: string; email_from_address?: string }) =>
        r.email_from_name ? `${r.email_from_name} <${r.email_from_address}>` : r.email_from_address ?? '';

    return (
        <AreaShell
            area="org"
            tab="whitelabel"
            sub="Custom gallery domains and the sender on guest share emails"
            actions={
                <button className="tb-new" onClick={openCreate}>
                    Add white-label
                </button>
            }
        >
            <p className="pg-sub" style={{ margin: '0 0 12px' }}>
                Galleries on a white-label domain use its sender: the From name and address change the “From” on every share email guests receive. Everything else uses the global gallery URL and sender.
            </p>
            <ListState
                rows={list.rows}
                failed={list.failed}
                what="white-label settings"
                reload={list.reload}
                empty={rows.length === 0 ? 'No white-labels yet. Galleries use the global URL and sender.' : undefined}
                emptyAction="Add white-label"
                onEmptyAction={openCreate}
            >
                <TCard>
                    {rows.map((r, i) => (
                        <TRow
                            key={r.id}
                            index={i}
                            name={r.name}
                            meta={fromLine(r)}
                            metaDate={stripScheme(r.domain)}
                            date={stripScheme(r.domain)}
                            status={r.email_reply_to_address && r.email_reply_to_address !== r.email_from_address ? `Replies → ${r.email_reply_to_address}` : ''}
                            onOpen={() => openEdit(r)}
                            menuItems={[
                                { label: 'Edit', onClick: () => openEdit(r) },
                                { label: 'Delete…', onClick: () => setDeleting(r) },
                            ]}
                        />
                    ))}
                </TCard>
            </ListState>
            {list.rows && !list.failed && rows.length > 0 && (
                <TFoot>{`${rows.length} ${rows.length === 1 ? 'domain' : 'domains'}`}</TFoot>
            )}

            <Dialog
                open={dialog !== null}
                title={dialog?.kind === 'edit' ? `Edit ${dialog.row.name}` : 'Add white-label'}
                onClose={close}
                onSubmit={submit}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={close} disabled={busy}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={busy}>
                            {busy ? 'Saving…' : dialog?.kind === 'create' ? 'Add white-label' : 'Save'}
                        </button>
                    </>
                }
            >
                <Field label="Name" help="For your reference, e.g. the client brand." error={show('name')}>
                    <TextInput autoFocus placeholder="Client brand" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                </Field>
                <Field label="Gallery domain" help="Full URL guests open their photos on." error={show('domain')}>
                    <TextInput inputMode="url" autoCapitalize="none" placeholder="https://photos.example.com" value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.target.value })} />
                </Field>
                <p className="s-lab" style={{ margin: '14px 0 4px' }}>Email sender</p>
                <p className="pg-sub" style={{ margin: '0 0 10px' }}>
                    These change the “From” on every share email guests receive from galleries on this domain.
                </p>
                <Field label="From name" help="Shown as the sender, e.g. “Client Events”.">
                    <TextInput placeholder="Client Events" value={draft.emailFromName} onChange={(e) => setDraft({ ...draft, emailFromName: e.target.value })} />
                </Field>
                <div className="two">
                    <Field label="From address" error={show('emailFromAddress')}>
                        <TextInput type="email" inputMode="email" placeholder="photos@example.com" value={draft.emailFromAddress} onChange={(e) => setDraft({ ...draft, emailFromAddress: e.target.value })} />
                    </Field>
                    <Field label="Reply-to (optional)" help="Defaults to the From address." error={show('emailReplyToAddress')}>
                        <TextInput type="email" inputMode="email" value={draft.emailReplyToAddress} onChange={(e) => setDraft({ ...draft, emailReplyToAddress: e.target.value })} />
                    </Field>
                </div>
                {error && <p className="field-e" role="alert" style={{ margin: '8px 0 0' }}>{error}</p>}
            </Dialog>

            <ConfirmDialog
                open={deleting !== null}
                title={deleting ? `Delete ${deleting.name}?` : ''}
                body={`Galleries on ${stripScheme(deleting?.domain)} fall back to the global URL, and share emails go back to the default sender. This can’t be undone.`}
                actionLabel="Delete"
                onConfirm={() => deleting && remove(deleting)}
                onClose={() => setDeleting(null)}
            />
            {toast.node}
        </AreaShell>
    );
}
